import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, Calendar, Mail, User } from 'lucide-react';
import { format } from 'date-fns';
import DailyNervView from './DailyNervView';
import AnomalySummarySection from './AnomalySummarySection';

interface OrganisationOverviewProps {
  organisation: any;
  onConnectionRemoved?: () => void;
  /** Opens the Settings tab (Daily NERV Trends "Change in Settings" link, FIG-409). */
  onOpenSettings?: () => void;
}

function hasEcommerceConnections(connections: Record<string, any> | null): boolean {
  const connectionList = getConnectionList(connections);
  return connectionList.some(isEcommerceFivetranConnection);
}

function getConnectionList(connections: Record<string, any> | null): any[] {
  if (!connections) return [];
  return Array.isArray(connections) ? connections : Object.values(connections);
}

function isCsvConnection(connection: any): boolean {
  if (!connection) return false;
  const service = (connection.service || '').toLowerCase();
  const type = (connection.type || '').toLowerCase();
  return (
    service === 'csv' ||
    type === 'csv' ||
    type === 'csv_import' ||
    connection.connectorId?.startsWith('csv-import-') ||
    connection.connectionId?.startsWith('csv-import-') ||
    connection.id?.startsWith('csv-import-') ||
    !!connection.csvMetadata ||
    (Array.isArray(connection.csvImports) && connection.csvImports.length > 0)
  );
}

function isEcommerceFivetranConnection(connection: any): boolean {
  if (!connection || isCsvConnection(connection)) return false;
  const service = (connection.service || '').toLowerCase();
  const type = (connection.type || '').toLowerCase();
  const connectorName = (connection.connector_name || '').toLowerCase();
  const name = (connection.name || '').toLowerCase();
  const lookup = `${service} ${type} ${connectorName} ${name}`;

  return lookup.includes('shopify') || lookup.includes('amazon');
}

const OrganisationOverview: React.FC<OrganisationOverviewProps> = ({ organisation, onConnectionRemoved, onOpenSettings }) => {
  const connections = getConnectionList(organisation?.connections);
  const hasAnyConnections = connections.length > 0;
  const hasOnlyCsvConnections = hasAnyConnections && connections.every(isCsvConnection);
  const hasEcommerceFivetranConnections = connections.some(isEcommerceFivetranConnection);
  const hideDailyNervForCsvOnlyOrg = hasOnlyCsvConnections && !hasEcommerceFivetranConnections;

  return (
    <div data-testid="organisation-overview" className="space-y-6">
      {/* Daily NERV Graph Section - Only show if financial snapshots enabled */}
      {organisation.financial_snapshot_table_created &&
        !hideDailyNervForCsvOnlyOrg &&
        (
          <DailyNervView organisation={organisation} onOpenSettings={onOpenSettings} />
        )}

      {/* Existing Overview Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5 text-blue-500" />
                Company Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">EIN</h3>
                  <p className="font-mono">{organisation.ein}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">State of Incorporation</h3>
                  <p>{organisation.state_of_incorporation}</p>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Address</h3>
                <p>{organisation.address}</p>
              </div>

              {organisation.revenue_last_12_months && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Revenue (last 12 months)</h3>
                  <p className="font-mono">
                    ${organisation.revenue_last_12_months.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </p>
                </div>
              )}

              <div>
                <h3 className="text-sm font-medium text-gray-500">Registration Date</h3>
                <p className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {organisation.created_at ? (
                    <>{format(new Date(organisation.created_at), 'MMMM d, yyyy')}</>
                  ) : (
                    <span className="text-gray-500">Not available</span>
                  )}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Anomaly Summary - ecommerce connections only, not shown for test clones */}
          {!organisation.is_test_clone && hasEcommerceConnections(organisation.connections) && (
            <AnomalySummarySection organisationId={organisation.id} orgCreatedAt={organisation.created_at} />
          )}
        </div>

        <OrganisationSidebar organisation={organisation} onConnectionRemoved={onConnectionRemoved} />
      </div>
    </div>
  );
};

const OrganisationSidebar: React.FC<OrganisationOverviewProps> = ({ organisation, onConnectionRemoved }) => {
  return (
    <div>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-blue-500" />
            Point of Contact
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Name</h3>
            <p>{organisation.poc_name}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">Email</h3>
            <p className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-400" />
              <a
                href={`mailto:${organisation.poc_email}`}
                className="text-blue-600 hover:underline"
              >
                {organisation.poc_email}
              </a>
            </p>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default OrganisationOverview;
export { OrganisationSidebar };
